import type { CitationOutput } from "@/components/tool-kit/source-citation-builder";
import {
  apaAuthors,
  apaInTextAuthors,
  type CitationParts,
  chicagoBibliographyAuthors,
  chicagoNoteAuthors,
  chicagoShortAuthors,
  mlaAuthors,
  mlaDate,
  mlaInTextAuthors,
  organizationName,
  type PartialDate,
  type PersonName,
  parsePartialDate,
  tidyParts,
  withPeriod,
} from "@/lib/tool-kit/citation-parts";
import {
  quotedTitle,
  shortNoteTitle,
  titleCase,
} from "@/lib/tool-kit/citation-sources-more/common";
import {
  capitalizeSubtitle,
  closer,
  get,
  italic,
  mlaPageRange,
  mlaUrl,
  plain,
} from "@/lib/tool-kit/legal-sources/common";
import {
  beforeSubtitle,
  fullUrl,
  longDate,
  type MediaLabels,
  type MediaValues,
  parsePeople,
} from "@/lib/tool-kit/media-sources/common";
import {
  chicagoPageRange,
  doiUrl,
  splitPages,
} from "@/lib/tool-kit/special-sources/common";

/*
 * An ERIC record — an ED document (a report, paper or other limited-circulation work
 * that ERIC holds) or an EJ journal article — in APA 7, MLA 9 and Chicago 18.
 *
 * Sources checked (October 2026):
 * - APA 7: the APA Style page "ERIC database references" and "Database information in
 *   references" (blocked to automated readers; read through Alamo Colleges' reprint and
 *   Northern Michigan University's guide). An ED document is a work of limited
 *   circulation, so the ED number goes in parentheses after the italic title, ERIC is
 *   the source (plain type), and the link is the full-text PDF when ERIC hosts it:
 *   "Brewster, C., & Railsback, J. (2002). Full-day kindergarten: Exploring an option
 *   for extended learning (ED472733). ERIC. https://files.eric.ed.gov/fulltext/
 *   ED472733.pdf" and "Riegelman, R. K., & Albertine, S. (2008). Recommendations for
 *   undergraduate public health education (ED504790). ERIC. …". An EJ record is a
 *   journal article of wide circulation: cite the article, with no ERIC.
 * - MLA 9: no ERIC example in the Handbook. Library guides (University of Portland,
 *   Valencia College) name ERIC as the database and give the ED number; this generator
 *   treats the document as a stand-alone work, with ERIC as the container and the ED
 *   number as its accession number, in the position MLA gives a number or location.
 * - Chicago 18: no ERIC example. The document is a report, cited as in CMOS 14 with
 *   "ERIC" and the accession number where a database or report series would go; an EJ
 *   article is a journal article (notes: "Journal 65, no. 2 (October 2007): 34–39").
 * - Facts used in the worked examples, checked against ERIC's own records (the public
 *   ERIC API, October 2026): ED472733, Cori Brewster and Jennifer Railsback, Full-Day
 *   Kindergarten: Exploring an Option for Extended Learning, Northwest Regional
 *   Educational Laboratory, December 2002; EJ777079, Carol S. Dweck, "The Perils and
 *   Promises of Praise", Educational Leadership, October 2007 (volume 65, issue 2, pages
 *   34–39 as the journal prints them).
 */

export const ERIC_FIELDS = {
  authors: "authors",
  organization: "organization",
  title: "title",
  date: "date",
  eric: "eric",
  publisher: "publisher",
  url: "url",
  journal: "journal",
  volume: "volume",
  issue: "issue",
  pages: "pages",
  doi: "doi",
} as const;

const F = ERIC_FIELDS;

/** An ED document or an EJ journal article. */
export type EricKind = "document" | "article";

export interface EricLabels extends MediaLabels {
  apaNote: string;
  apaArticleNote: string;
  mlaNote: string;
  chicagoNote: string;
}

interface EricRecord {
  kind: EricKind;
  authors: PersonName[];
  title: string;
  date: PartialDate | null;
  eric: string;
  publisher: string;
  url: string;
  journal: string;
  volume: string;
  issue: string;
  pages: string;
  doi: string;
}

const RE_EN_DASH = /–/g;
const RE_SPACES = /\s+/g;
const RE_ERIC_NUMBER = /^E[DJ]\d+$/i;

/** "ed 472733" → "ED472733"; anything that is not an ED or EJ number is kept as typed. */
export function normalizeEricNumber(text: string): string {
  const compact = text.replace(RE_SPACES, "");
  return RE_ERIC_NUMBER.test(compact) ? compact.toUpperCase() : text.trim();
}

function readAuthors(values: MediaValues): PersonName[] {
  const text = get(values, F.authors);
  if (get(values, F.organization) === "true") {
    const first = text.split("\n").find((line) => line.trim() !== "");
    return first ? [organizationName(first)] : [];
  }
  return parsePeople(text);
}

function readRecord(kind: EricKind, values: MediaValues): EricRecord {
  const article = kind === "article";
  const doi = article ? (doiUrl(get(values, F.doi)) ?? "") : "";
  const typed = fullUrl(get(values, F.url));
  return {
    kind,
    authors: readAuthors(values),
    title: capitalizeSubtitle(get(values, F.title)),
    date: parsePartialDate(get(values, F.date)),
    eric: normalizeEricNumber(get(values, F.eric)),
    publisher: article ? "" : get(values, F.publisher),
    url: doi || typed,
    journal: article ? get(values, F.journal) : "",
    volume: article ? get(values, F.volume) : "",
    issue: article ? get(values, F.issue) : "",
    pages: article ? get(values, F.pages) : "",
    doi,
  };
}

/** The record's own page, used for a document when no full-text address is typed. */
function documentUrl(record: EricRecord): string {
  if (record.url) {
    return record.url;
  }
  return record.eric ? `https://eric.ed.gov/?id=${record.eric}` : "";
}

/* ---------------------------------------------------------------------- APA */

function apaPages(text: string): string {
  const [from, to] = splitPages(text);
  return to ? `${from}–${to}` : from;
}

function apaDocument(record: EricRecord): CitationParts {
  const year = record.date ? String(record.date.year) : "n.d.";
  const number = record.eric ? ` (${record.eric})` : "";
  const tail = ["ERIC.", documentUrl(record)].filter(Boolean).join(" ");
  if (record.authors.length === 0) {
    return tidyParts([
      italic(record.title),
      plain(`${number}. (${year}). ${tail}`),
    ]);
  }
  const names = apaAuthors(record.authors);
  return tidyParts([
    plain(`${names}${closer(names)} (${year}). `),
    italic(record.title),
    plain(`${number}. ${tail}`),
  ]);
}

function apaArticle(record: EricRecord): CitationParts {
  const year = record.date ? String(record.date.year) : "n.d.";
  const volume = record.volume ? `, ${record.volume}` : "";
  const issue = record.issue ? `(${record.issue})` : "";
  const pages = record.pages ? `, ${apaPages(record.pages)}` : "";
  const names = apaAuthors(record.authors);
  const title = record.title;
  return tidyParts([
    plain(`${names}${closer(names)} (${year}). ${title}${closer(title)} `),
    italic(`${record.journal}${volume}`),
    plain(`${issue}${pages}.${record.url ? ` ${record.url}` : ""}`),
  ]);
}

function apaOutput(record: EricRecord, labels: EricLabels): CitationOutput {
  const year = record.date ? String(record.date.year) : "n.d.";
  const has = record.authors.length > 0;
  const short = beforeSubtitle(titleCase(record.title));
  return {
    key: "apa",
    label: labels.apa,
    entries: [
      {
        label: labels.referenceList,
        parts:
          record.kind === "document" ? apaDocument(record) : apaArticle(record),
      },
      {
        label: labels.parenthetical,
        parts: has
          ? [plain(`(${apaInTextAuthors(record.authors)}, ${year})`)]
          : [plain(`("${short}", ${year})`)],
      },
      {
        label: labels.narrative,
        parts: has
          ? [plain(`${apaInTextAuthors(record.authors, true)} (${year})`)]
          : [plain(`"${short}" (${year})`)],
      },
    ],
    note: record.kind === "document" ? labels.apaNote : labels.apaArticleNote,
  };
}

/* ---------------------------------------------------------------------- MLA */

function mlaDocument(record: EricRecord): CitationParts {
  const title = titleCase(record.title);
  const imprint = [record.publisher, mlaDate(record.date)]
    .filter(Boolean)
    .join(", ");
  const url = documentUrl(record);
  const location = [record.eric, url ? mlaUrl(url) : ""]
    .filter(Boolean)
    .join(", ");
  const parts: CitationParts = [];
  if (record.authors.length > 0) {
    const names = mlaAuthors(record.authors);
    parts.push(plain(`${names}${closer(names)} `));
  }
  parts.push(
    italic(title),
    plain(`${closer(title)}${imprint ? ` ${withPeriod(imprint)}` : ""} `)
  );
  parts.push(italic("ERIC"), plain(`${location ? `, ${location}` : ""}.`));
  return tidyParts(parts);
}

function mlaArticle(record: EricRecord): CitationParts {
  const title = titleCase(record.title);
  const [from, to] = splitPages(record.pages);
  const pages = from
    ? `${to ? "pp." : "p."} ${mlaPageRange(from, to).replace(RE_EN_DASH, "-")}`
    : "";
  const elements = [
    record.volume ? `vol. ${record.volume}` : "",
    record.issue ? `no. ${record.issue}` : "",
    mlaDate(record.date),
    pages,
  ].filter(Boolean);
  const parts: CitationParts = [];
  if (record.authors.length > 0) {
    const names = mlaAuthors(record.authors);
    parts.push(plain(`${names}${closer(names)} `));
  }
  parts.push(
    plain(`${quotedTitle(title, ".")} `),
    italic(record.journal),
    plain(`${elements.length > 0 ? `, ${elements.join(", ")}` : ""}.`)
  );
  if (record.url) {
    const link = record.doi ? record.doi : mlaUrl(record.url);
    parts.push(plain(` ${link}.`));
  } else if (record.eric) {
    parts.push(plain(" "), italic("ERIC"), plain(`, ${record.eric}.`));
  }
  return tidyParts(parts);
}

function mlaOutput(record: EricRecord, labels: EricLabels): CitationOutput {
  const [first] = splitPages(record.pages);
  const stamp = first && record.kind === "article" ? ` ${first}` : "";
  const who =
    record.authors.length > 0
      ? mlaInTextAuthors(record.authors)
      : quotedTitle(beforeSubtitle(titleCase(record.title)), "");
  return {
    key: "mla",
    label: labels.mla,
    entries: [
      {
        label: labels.worksCited,
        parts:
          record.kind === "document" ? mlaDocument(record) : mlaArticle(record),
      },
      { label: labels.inText, parts: [plain(`(${who}${stamp})`)] },
    ],
    note: labels.mlaNote,
  };
}

/* ------------------------------------------------------------------ Chicago */

function chicagoDocumentNote(record: EricRecord, title: string): CitationParts {
  const lead =
    record.authors.length > 0 ? `${chicagoNoteAuthors(record.authors)}, ` : "";
  const imprint = [record.publisher, record.date?.year ?? ""]
    .filter(Boolean)
    .join(", ");
  const url = documentUrl(record);
  const tail = [record.eric ? `ERIC ${record.eric}` : "ERIC", url]
    .filter(Boolean)
    .join(", ");
  return tidyParts([
    plain(lead),
    italic(title),
    plain(`${imprint ? ` (${imprint})` : ""}, ${tail}.`),
  ]);
}

function chicagoArticleLocator(record: EricRecord): string {
  const [from, to] = splitPages(record.pages);
  const issue = record.issue ? `, no. ${record.issue}` : "";
  const volume = record.volume ? ` ${record.volume}${issue}` : "";
  const date = longDate(record.date);
  const month = date ? ` (${date})` : "";
  const pages = from ? `: ${chicagoPageRange(from, to)}` : "";
  return `${volume}${month}${pages}`;
}

function chicagoArticleLink(record: EricRecord): string {
  if (record.url) {
    return record.url;
  }
  return record.eric ? `ERIC ${record.eric}` : "";
}

function chicagoArticleNote(record: EricRecord, title: string): CitationParts {
  const lead =
    record.authors.length > 0 ? `${chicagoNoteAuthors(record.authors)}, ` : "";
  const link = chicagoArticleLink(record);
  return tidyParts([
    plain(`${lead}${quotedTitle(title, ",")} `),
    italic(record.journal),
    plain(`${chicagoArticleLocator(record)}${link ? `, ${link}` : ""}.`),
  ]);
}

function chicagoShortNote(record: EricRecord, title: string): CitationParts {
  const short = shortNoteTitle(title);
  const lead =
    record.authors.length > 0 ? `${chicagoShortAuthors(record.authors)}, ` : "";
  if (record.kind === "document") {
    return tidyParts([plain(lead), italic(short), plain(".")]);
  }
  return [plain(`${lead}${quotedTitle(short, ".")}`)];
}

function chicagoDocumentBibliography(
  record: EricRecord,
  title: string
): CitationParts {
  const names =
    record.authors.length > 0 ? chicagoBibliographyAuthors(record.authors) : "";
  const imprint = [record.publisher, record.date?.year ?? ""]
    .filter(Boolean)
    .join(", ");
  const url = documentUrl(record);
  const tail = [
    `${record.eric ? `ERIC ${record.eric}` : "ERIC"}.`,
    url ? `${url}.` : "",
  ]
    .filter(Boolean)
    .join(" ");
  return tidyParts([
    plain(names ? `${names}${closer(names)} ` : ""),
    italic(title),
    plain(
      `${closer(title)}${imprint ? ` ${withPeriod(imprint)}` : ""} ${tail}`
    ),
  ]);
}

function chicagoArticleBibliography(
  record: EricRecord,
  title: string
): CitationParts {
  const names =
    record.authors.length > 0 ? chicagoBibliographyAuthors(record.authors) : "";
  const link = chicagoArticleLink(record);
  return tidyParts([
    plain(
      `${names ? `${names}${closer(names)} ` : ""}${quotedTitle(title, ".")} `
    ),
    italic(record.journal),
    plain(`${chicagoArticleLocator(record)}${link ? `. ${link}` : ""}.`),
  ]);
}

function chicagoOutput(record: EricRecord, labels: EricLabels): CitationOutput {
  const title = titleCase(record.title);
  const document = record.kind === "document";
  return {
    key: "chicago",
    label: labels.chicago,
    entries: [
      {
        label: labels.footnote,
        parts: document
          ? chicagoDocumentNote(record, title)
          : chicagoArticleNote(record, title),
      },
      { label: labels.shortNote, parts: chicagoShortNote(record, title) },
      {
        label: labels.bibliography,
        parts: document
          ? chicagoDocumentBibliography(record, title)
          : chicagoArticleBibliography(record, title),
      },
    ],
    note: labels.chicagoNote,
  };
}

export function ericIsReady(values: MediaValues): boolean {
  return get(values, F.title) !== "";
}

export function formatEric(
  kind: EricKind,
  values: MediaValues,
  labels: EricLabels
): CitationOutput[] {
  const record = readRecord(kind, values);
  return [
    apaOutput(record, labels),
    mlaOutput(record, labels),
    chicagoOutput(record, labels),
  ];
}
